// Записът на листа „Инвентаризация" (изнесен от страницата, за да е тя под 500
// реда): пречките (`buildStocktakeIssues` + пречките на приготвените рецепти),
// payload-ът и мутацията — нова или редакция. При пречка Ctrl/Cmd+Enter казва
// първата в съобщение и, ако тя сочи клетка (ед. цена на излишък без източник,
// „Намерено" на съставка без насипно преброено, „Порции" на рецепта), я
// фокусира — страницата разкрива реда, ако филтър го крие. След запис — към
// началния екран с `{openDoc}` (toast „Виж документа").
import type { MutableRefObject } from "react";
import { App } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCreateStocktake, useUpdateStocktake } from "../../../../hooks/useInventory";
import type { CreateStocktakeLine, CreateStocktakePayload, Stocktake } from "../../../../api/inventory";
import type { CreateStocktakeRecipe } from "../../../../api/inventoryStocktakes";
import type { GridRowKey } from "../../components/gridNavigation";
import { withCurrentTime } from "../../components/dateTime";
import {
  STOCKTAKE_COLS,
  toSubstitutionItems,
  type StocktakeComputedRow,
  type SubstitutionProposal,
} from "./stocktakeWorksheetShared";
import { buildStocktakeIssues } from "./stocktakeIssues";
import { buildRecipeIssues, recipeRowKey, type StocktakeRecipeRow } from "./stocktakeRecipes";

type Params = {
  mode: "new" | "edit";
  stocktakeId: string | undefined;
  homePath: string;
  warehouseId: number | null;
  date: string; // YYYY-MM-DD
  reason: string;
  countedRows: StocktakeComputedRow[];
  buildLines: () => CreateStocktakeLine[];
  proposals: SubstitutionProposal[];
  appliedDonorByRecipient: Map<number, number>;
  dirtyRef: MutableRefObject<boolean>;
  // Приготвените рецепти: редовете, съставките без насипно преброено и тялото
  recipes?: {
    rows: StocktakeRecipeRow[];
    uncountedIngredients: { productId: number; name: string }[];
    payload: () => CreateStocktakeRecipe[];
  };
  // записана НОВА инвентаризация → черновата изчезва (useWorksheetDraft.onSaved)
  onSaved: () => void;
  // пречката сочи клетка → разкрий реда и фокусирай клетката (продукт — по id,
  // рецепта — по ключа `r12`)
  onFocusIssue: (rowKey: GridRowKey, col: number) => void;
};

export const useStocktakeSave = ({
  mode,
  stocktakeId,
  homePath,
  warehouseId,
  date,
  reason,
  countedRows,
  buildLines,
  proposals,
  appliedDonorByRecipient,
  dirtyRef,
  recipes,
  onSaved,
  onFocusIssue,
}: Params) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const createMutation = useCreateStocktake();
  const updateMutation = useUpdateStocktake();
  const pending = createMutation.isPending || updateMutation.isPending;
  const recipeIssues = recipes
    ? buildRecipeIssues({ rows: recipes.rows, uncountedIngredients: recipes.uncountedIngredients, t })
    : [];
  const issues = buildStocktakeIssues({ warehouseId, date, countedRows, t, recipeIssues });

  const submit = () => {
    if (pending) return;
    const firstIssue = issues[0];
    if (firstIssue) {
      message.error(firstIssue.text);
      if (firstIssue.focusRecipeId != null)
        onFocusIssue(recipeRowKey(firstIssue.focusRecipeId), STOCKTAKE_COLS.counted);
      else if (firstIssue.focus) onFocusIssue(firstIssue.focus.productId, firstIssue.focus.col);
      return;
    }
    if (!warehouseId) return;
    const recipesPayload = recipes?.payload() ?? [];
    const payload: CreateStocktakePayload = {
      warehouseId,
      date: (withCurrentTime(dayjs(date)) ?? dayjs()).toISOString(),
      reason: reason.trim() || null,
      lines: buildLines(),
      substitutions: toSubstitutionItems(proposals, appliedDonorByRecipient),
      // само когато има — стар BE без рецепти не вижда ново поле
      ...(recipesPayload.length ? { recipes: recipesPayload } : {}),
    };
    // Началният екран показва toast „Инвентаризация №X е записана · Виж документа"
    const onSuccess = (saved: Stocktake) => {
      onSaved();
      dirtyRef.current = false; // запазено → напускането не пита
      navigate(homePath, { state: { openDoc: saved } });
    };
    const onError = (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } };
      message.error(err.response?.data?.detail || t("inventoryPage.errStocktake"));
    };
    if (mode === "edit") {
      updateMutation.mutate({ id: Number(stocktakeId), payload }, { onSuccess, onError });
    } else {
      createMutation.mutate(payload, { onSuccess, onError });
    }
  };

  return { issues, submit, pending };
};
